/**
 * Issues View
 * Performance analysis and betting insights
 *
 * This view uses a hybrid approach for insights:
 * - Rule-based analysis: Deterministic stats and issue detection
 * - AI-enhanced analysis: Natural language synthesis and personalized advice (when available)
 */

import { LoadingSpinner } from '@components/ui';
import { AnalyticsFilterBar } from '@components/ui/AnalyticsFilterBar';
import { useAuth } from '@contexts/AuthContext';
import { useInsights } from '@hooks/useInsights';
import type { AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import { RefreshCw, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { GeneralInsights } from './issues/GeneralInsights';
import { LegLevelAnalysis } from './issues/LegLevelAnalysis';
import { ParlayLevelAnalysis } from './issues/ParlayLevelAnalysis';

// =============================================================================
// Types
// =============================================================================

type ViewMode = 'parlay' | 'leg';

// =============================================================================
// Main Component
// =============================================================================

interface IssuesViewProps {
  filter: AnalyticsFilter;
  onFilterChange: (filter: AnalyticsFilter) => void;
}

export function IssuesView({ filter, onFilterChange }: IssuesViewProps): ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>('parlay');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const { user, updatePreferences } = useAuth();
  const { insights, issues, globalInsights, loading, refreshing, refreshInsights, dismissIssue } =
    useInsights(filter);

  // Handle refresh with error display
  const handleRefresh = async () => {
    setRefreshError(null);
    const result = await refreshInsights();
    if (!result.success && result.error) {
      setRefreshError(result.error);
    }
  };

  // Get current min sample size from user preferences
  const minSampleSize = user?.preferences?.minSampleSizeForInsights ?? 3;

  // Handle min sample size change - update preference and trigger recompute
  const handleMinSampleSizeChange = async (value: number) => {
    if (value === minSampleSize) return;
    setSavingPrefs(true);
    try {
      await updatePreferences({ minSampleSizeForInsights: value });
      // Trigger recompute after preference is saved
      await refreshInsights();
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" message="Loading insights..." />;
  }

  return (
    <div className="space-y-6">
      <AnalyticsFilterBar showFilter filter={filter} onFilterChange={onFilterChange} />

      {/* View Toggle + Refresh Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-theme-surface rounded-lg p-2">
          <button
            type="button"
            data-testid="issues-parlay-level-tab"
            onClick={() => setViewMode('parlay')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition ${
              viewMode === 'parlay'
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'text-theme-text-tertiary hover:text-white'
            }`}
          >
            Parlay Level
          </button>
          <button
            type="button"
            data-testid="issues-leg-level-tab"
            onClick={() => setViewMode('leg')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition ${
              viewMode === 'leg'
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'text-theme-text-tertiary hover:text-white'
            }`}
          >
            Leg Level
          </button>
        </div>

        {insights && (
          <button
            type="button"
            data-testid="issues-refresh-button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-theme-surface hover:bg-theme-surface/80 rounded text-sm text-theme-text-secondary transition"
          >
            {refreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </>
            )}
          </button>
        )}
      </div>

      {/* Refresh Error Display */}
      {refreshError && (
        <div className="bg-orange-500/30 border border-orange-500 rounded-lg p-3 flex items-center justify-between">
          <span className="text-orange-400 text-sm">{refreshError}</span>
          <button
            type="button"
            onClick={() => setRefreshError(null)}
            className="text-orange-400 hover:text-orange-400 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {viewMode === 'parlay' ? (
        <ParlayLevelAnalysis insights={insights} issues={issues} dismissIssue={dismissIssue} />
      ) : (
        <LegLevelAnalysis
          insights={insights}
          minSampleSize={minSampleSize}
          onMinSampleSizeChange={handleMinSampleSizeChange}
          isSaving={savingPrefs || refreshing}
        />
      )}

      <GeneralInsights globalInsights={globalInsights} insights={insights} />
    </div>
  );
}
